import * as React from "react";
import { inject, observer } from "mobx-react";

import { withLocalization } from "@shared-ui/localization-context";
import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import { logger } from "bernie-core";

import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { WizardRegionHotwireContentProps, WizardRegionHotwireProps } from "./typings";
import { childContainsKey } from "./utils/WizardRegionHotwireUtil";
import { DesktopContent } from "./components/DesktopContent";
import { MobileContent } from "./components/MobileContent";
import {
  getWizardLOBFlexComponents,
  getWizardLOBFormChildren,
} from "src/components/shared/StorefrontWizard/StorefrontWizardUtils";
import { MultiLOBWizardRegionContext } from "./WizardRegionContextProvider/MultiLOBWizardRegionContext";
import { LobQueryRouter } from "src/components/shared/StorefrontWizard/LobQueryRouter";
import {
  TabbedWizardCard,
  WizardRegionType,
} from "src/components/shared/StorefrontWizard/TabbedWizardCard/TabbedWizardCard";
import { useHotwireCarOverrides } from "components/flexComponents/WizardCarPWA/utils/hotwireCarOverrides";
import { useHotwireHotelOverrides } from "components/flexComponents/WizardHotelPWAv2/utils/hotwireHotelOverrides";
import { useHotwireFlightOverrides } from "components/flexComponents/WizardFlightPWA/utils/hotwireFlightOverrides";
import { useHotwirePackageOverrides } from "components/flexComponents/WizardPackagePWA/utils/hotwirePackageOverrides";
import { ClientWizardStateStore } from "stores/wizard/ClientWizardStateStore";

interface LineOfBusinessParams {
  wizardFormChildren: React.ReactElement[];
  wizardState: ClientWizardStateStore;
  isMultiLOB: boolean;
}

/**
 * Get the LOB tracking string from the current wizard state: either the lob (hotel/car/flight/package) or "unknown".
 */
const getLineOfBusiness = ({ wizardFormChildren, wizardState, isMultiLOB }: LineOfBusinessParams) => {
  const wizardKey = (isMultiLOB && wizardState.activeLob) || wizardFormChildren?.[0]?.key;

  return wizardKey?.toString()?.replace("wizard-", "")?.split("-")?.[0] || "unknown";
};

/**
 * This region, based on StorefrontWizardRegionCOMET but simplified for a MVP use-case,
 * expects one wizard component to be added. (for now, it does not support multiple LOBs).
 */
export const WizardRegionHotwire = withLocalization(
  logger("WizardRegionHotwire")(
    inject(
      "context",
      "wizardState"
    )(
      observer((props: WizardRegionHotwireProps) => {
        const { flexComponent, context, wizardState, logger, l10n } = props;
        const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);

        useHotwireHotelOverrides(wizardState.hotelWizardState);
        useHotwireCarOverrides(wizardState.carWizardState);
        useHotwireFlightOverrides(wizardState.flightWizardState);
        useHotwirePackageOverrides(wizardState.packageWizardState);

        wizardState.globalWizardState.setIsDesktop(context.deviceInformation);

        if (!flexComponent || !flexComponent.model || !blossomComponent) {
          return null;
        }

        const children = blossomComponent.renderChildren(logger, l10n, context);

        if (children.length < 1) {
          return null;
        }

        const wizardFormChildren = getWizardLOBFormChildren(children);
        const isMultiLOB = wizardFormChildren?.length > 1;

        const wizardLOBs = getWizardLOBFlexComponents(flexComponent.modules);

        const { model } = flexComponent;
        const wizard = wizardFormChildren[0];
        const destinationHero = children.find((child) => childContainsKey(child, "destination-hero"));
        const lineOfBusiness = getLineOfBusiness({ wizardFormChildren, wizardState, isMultiLOB });

        const contentProps: WizardRegionHotwireContentProps = {
          lineOfBusiness,
          model,
          destinationHero,
          wizard: isMultiLOB ? (
            <TabbedWizardCard
              wizardFormChildren={wizardFormChildren}
              wizardState={wizardState}
              wizardRegionType={WizardRegionType.BEX}
              wrappingClassNames="SimpleContainer"
              showTabsIcon={false}
            />
          ) : (
            wizard
          ),
        };

        return (
          <MultiLOBWizardRegionContext.Provider value={{ isMultiLOB }}>
            <LobQueryRouter lobs={wizardLOBs} />
            <Viewport>
              <ViewSmall>
                <MobileContent {...contentProps} />
              </ViewSmall>
              <ViewMedium>
                <DesktopContent {...contentProps} />
              </ViewMedium>
            </Viewport>
          </MultiLOBWizardRegionContext.Provider>
        );
      })
    )
  )
);

export default WizardRegionHotwire;
